/**
 * PageErrorBoundary — wraps individual pages so a render exception in one
 * panel doesn't blank the whole route. Surfaces a friendly fallback with
 * a one-tap "Reload page" + "Force refresh" pair so the user can self-heal
 * without dev tools.
 *
 * Feb 12 2026 — iOS Safari stale-bundle hardening pass.
 *   Symptom: deploy lands → user opens the app from their home screen →
 *   service worker / cache still serves the previous index.html which
 *   references hashed JS chunks the new deploy no longer ships. React
 *   throws a chunk-load error mid-render, ErrorBoundary catches it, the
 *   prior `setTimeout(reload, 250)` reloaded but reload still hit the
 *   cached service worker → same crash → infinite snag screen.
 *
 *   Fix: on FIRST catch in a 60-second window, do a *cache-busted hard
 *   refresh* (unregister service workers + clear cache storage + reload
 *   with timestamp query) instead of a soft reload. This guarantees the
 *   fresh index.html lands on the second paint.
 *
 *   Also: bump the auto-recovery cap to ensure the throwing chunk
 *   actually leaves the in-memory module cache. Detect chunk-load
 *   errors specifically so we don't hard-refresh on a legitimate bug.
 */
import React from 'react';

const KEY = 'sy_pe_boundary_reloaded_at';
const HARD_RELOAD_DONE_KEY = 'sy_pe_boundary_hard_reloaded_at';

// Heuristic — Webpack chunk-load errors share this signature across
// Chrome/Safari/Firefox. We use it to decide whether to hard-refresh
// (caches/SW + cache-busted reload) vs soft-reload.
const isChunkLoadError = (err) => {
  if (!err) return false;
  const msg = String(err.message || err);
  const name = err.name || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

const clearCachesAndReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
    }
  } catch (_) { /* best-effort */ }
  try {
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch (_) { /* best-effort */ }
  // Cache-bust the index.html itself so iOS Safari can't serve a stale
  // copy from the back-forward cache (bfcache) either.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_r', String(Date.now()));
    window.location.replace(u.toString());
    return;
  } catch (_) {
    /* URL API unavailable — fall back */
  }
  window.location.reload();
};

class PageErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Always log so the symptom shows up in the user's console + our
    // remote telemetry pipeline (if wired). info?.componentStack is the
    // most useful breadcrumb for production debugging.
    console.error('[PageErrorBoundary]', error, info?.componentStack);

    // Auto-recover once per 60s window. We split into two recovery
    // paths because they're idempotent at very different cost levels:
    //   1. Chunk-load → cache+SW clear + cache-busted reload (heavier)
    //   2. Any other render error → soft reload (lighter)
    // We also track each path independently so a real bug doesn't loop
    // through a hard refresh forever.
    try {
      const now = Date.now();
      if (isChunkLoadError(error)) {
        const lastHard = Number(sessionStorage.getItem(HARD_RELOAD_DONE_KEY) || 0);
        if (now - lastHard > 60_000) {
          sessionStorage.setItem(HARD_RELOAD_DONE_KEY, String(now));
          setTimeout(clearCachesAndReload, 250);
          return;
        }
      }
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (now - last > 60_000) {
        sessionStorage.setItem(KEY, String(now));
        setTimeout(() => window.location.reload(), 250);
      }
    } catch (_) {
      /* sessionStorage may be unavailable in private mode — no-op */
    }
  }

  hardRefresh = async () => {
    await clearCachesAndReload();
  };

  goHome = () => {
    try {
      // Drop any persisted UI state that might re-throw on next render
      // (e.g. admin viewing-as set to a removed value, stale persona).
      ['adminViewingAs', 'writerActiveSpace'].forEach((k) => {
        try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
      });
    } catch (_) { /* ignore */ }
    try {
      sessionStorage.removeItem(KEY);
      sessionStorage.removeItem(HARD_RELOAD_DONE_KEY);
    } catch (_) { /* ignore */ }
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center" data-testid="page-error-boundary">
        <div className="w-14 h-14 rounded-full bg-rose-500/20 border border-rose-400/40 flex items-center justify-center text-2xl mb-4">
          ⚠️
        </div>
        <h2 className="text-xl font-bold text-white mb-2">This page hit a snag</h2>
        <p className="text-sm text-slate-400 max-w-md mb-5">
          We&apos;re auto-recovering. If this screen sticks, tap below to clear the cache and reload — that fixes 99% of these.
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold"
            data-testid="page-error-reload"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.hardRefresh}
            className="px-4 py-2 rounded-full bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold"
            data-testid="page-error-hard-refresh"
          >
            Force refresh
          </button>
          <button
            type="button"
            onClick={this.goHome}
            className="px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold"
            data-testid="page-error-go-home"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
